import { createContext, useContext, ReactNode } from 'react'

// Define the possible website versions
export type WebsiteVersion = 'default' | 'v1' | 'v2'

// Define the context shape
interface VersionContextType {
  version: WebsiteVersion
  isV1: boolean
  isV2: boolean
  isDefault: boolean
}

// Create the context with a default value
const VersionContext = createContext<VersionContextType>({
  version: 'default',
  isV1: false,
  isV2: false,
  isDefault: true,
})

// Custom hook to use the version context
export const useVersion = () => {
  const context = useContext(VersionContext)
  if (!context) {
    throw new Error('useVersion must be used within a VersionProvider')
  }
  return context
}

// Provider component props
interface VersionProviderProps {
  children: ReactNode
}

// Version Provider component
export function VersionProvider({ children }: VersionProviderProps) {
  // Determine version from environment variable
  // If VITE_WEBSITE_VERSION is set to 'v1' or 'v2', use that version, otherwise use default
  const envVersion = (import.meta as any).env?.VITE_WEBSITE_VERSION
  const version: WebsiteVersion = 
    envVersion === 'v1' ? 'v1' : 
    envVersion === 'v2' ? 'v2' : 
    'default'
  
  // Computed helper values
  const isV1 = version === 'v1'
  const isV2 = version === 'v2'
  const isDefault = version === 'default'

  // Log current version in development
  if ((import.meta as any).env?.DEV) {
    console.log(`🎛️ Website Version: ${version}`)
  }

  const contextValue: VersionContextType = {
    version,
    isV1,
    isV2,
    isDefault,
  }

  return (
    <VersionContext.Provider value={contextValue}>
      {children}
    </VersionContext.Provider>
  )
}
